import { inject, Injectable, Injector, isSignal, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from '@angular/router';
import { isObservable, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, concatMap, map, skipWhile, switchMap, tap } from 'rxjs/operators';
import { UnsavedChangesDialog, UnsavedChangesDialogResult } from 'src/app/shared/dialogs/unsaved-changes/unsaved-changes.dialog';
import { isNullOrUndefined } from '../utils';
import { logger } from '../../pbir/utils/logger';

const LOGGER = logger('UnsavedChanges');

export interface UnsavedChanges {
  /** Returns whether unsaved changes can be discard (e.g. existing item) */
  readonly canDiscardUnsavedChanges: Signal<boolean> | (() => Observable<boolean> | boolean);

  /** Returns whether saving is currently allowed. */
  readonly canSave: Signal<boolean> | (() => Observable<boolean> | boolean);

  /** Returns whether the component has any unsaved changes */
  readonly hasUnsavedChanges: Signal<boolean> | (() => Observable<boolean> | boolean);

  /** Handles unsaved changes.
   * Should return a boolean indicating whether to continue. 
   * @returns Boolean indicating whether the unsaved changes have been handled. */
  handleUnsavedChanges(result: UnsavedChangesDialogResult):
    Observable<boolean>
    | Promise<boolean>
    | boolean
    | void;
}

@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesGuard implements CanDeactivate<UnsavedChanges> {
  readonly injector = inject(Injector);

  constructor(public dialog: MatDialog) { }

  canDeactivate(
    component: UnsavedChanges,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot) {
    let hasUnsavedChanges$ = this.unwrap(component, component.hasUnsavedChanges, false);

    return hasUnsavedChanges$.pipe(
      switchMap((hasUnsavedChanges) => {
        if (hasUnsavedChanges === undefined || hasUnsavedChanges === false) {
          return of(true);
        }

        let canContinue$ = new Subject<boolean>();

        let dialogRef = this.dialog.open(UnsavedChangesDialog, {
          width: '450px',
          data: {
            canDiscard: component.canDiscardUnsavedChanges() ?? true,
            canSave: component.canSave() ?? true,
          },
          disableClose: true
        });

        dialogRef.afterClosed().pipe(
          switchMap((result: UnsavedChangesDialogResult) => {
            let canExit = result !== UnsavedChangesDialogResult.Cancel;

            let isSaving$: Observable<boolean>;

            try {
              isSaving$ = this.unwrap(component, component.handleUnsavedChanges, true, result);
            }
            catch (err) {
              LOGGER.error(`Failed when handling unsaved changes`, err);
            }

            return isSaving$.pipe(
              skipWhile(b => !b),
              tap(() => {
                canContinue$.next(canExit);
                canContinue$.complete();
              }),
              catchError(err => {
                LOGGER.error(`Failed when handling unsaved changes`, err);
                canContinue$.next(false);
                canContinue$.complete();
                return of(false);
              })
            );
          })
        ).subscribe();

        return canContinue$;
      }),
    );
  }

  private unwrap(_this: any, b: any, defaultValue: boolean, ...args: any[]): Observable<boolean> {
    if (isNullOrUndefined(b))
      return of(defaultValue);

    if (typeof b === 'boolean')
      return of(b);

    if (isSignal(b))
      return <Observable<boolean>>toObservable(b, { injector: this.injector });

    if (isObservable(b))
      return <Observable<boolean>>b;

    if (typeof b === 'function')
      return this.unwrap(_this, b.apply(_this, args), defaultValue);

    throw new Error(`Unsupported value`);
  }
}
